// Workflows (design/isomorphic workflows): a card per workflow, since each is a small
// program (prompt, trigger, policy), with its last-30-days figures and "Run now".
import { FlowArrow, Play } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Button } from "rizzui";

import type { Run, Workflow } from "../api/client";
import { environmentsQuery, runsQuery, workflowsQuery, workspacesQuery } from "../api/queries";
import { useT } from "../i18n";
import { useCurrentOrg } from "../layout/currentOrg";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { dayTime, usd } from "../workloads/format";
import { TriggerIcon } from "../workloads/TriggerIcon";
import { parseTrigger, triggerLabel } from "../workloads/policy";
import { workflowStats } from "../workloads/runs";
import { useTriggerRun } from "../workloads/useTriggerRun";
import { EnabledMark, WorkflowBadges } from "../workloads/workflow/WorkflowBadges";
import { sectionCrumbs } from "../layout/nav";

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="tabular font-display text-lg font-bold text-gray-900">{value}</div>
      <div className="text-[11.5px] text-gray-500">{label}</div>
    </div>
  );
}

function WorkflowCard({ workflow, runs, where }: { workflow: Workflow; runs: Run[]; where: string }) {
  const t = useT().workloads.legacy;
  const stats = workflowStats(runs);
  const trigger = useTriggerRun();
  return (
    <section className={cn(cardClass, "flex flex-col p-5")} aria-label={workflow.name}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-lighter text-primary">
            <TriggerIcon trigger={workflow.trigger} className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <Link to={`/legacy-workflows/${workflow.id}`} className="block truncate font-display text-base font-bold text-gray-900 hover:text-primary">
              {workflow.name}
            </Link>
            <div className="truncate text-xs text-gray-500">{[where, triggerLabel(parseTrigger(workflow.trigger))].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <EnabledMark enabled={workflow.enabled} />
      </div>
      <p className="mb-0 mt-4 line-clamp-2 text-[13px] leading-relaxed text-gray-600">{workflow.prompt}</p>
      <div className="mt-4">
        <WorkflowBadges workflow={workflow} />
      </div>
      <div className="mt-auto">
        <div className="mt-5 grid grid-cols-3 gap-3 border-t border-muted pt-4 text-center">
          <Stat value={String(stats.runs)} label={t.runs30d} />
          <Stat value={stats.successPct === undefined ? "—" : `${stats.successPct}%`} label={t.succeeded} />
          <Stat value={usd(stats.avgCost)} label={t.avgCost} />
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{stats.lastRunAt === undefined ? t.noRunsYet : t.lastRun(dayTime(stats.lastRunAt))}</span>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={!workflow.enabled || trigger.isPending} onClick={() => trigger.mutate(workflow.id)}>
            <Play weight="fill" className="h-3.5 w-3.5" /> {t.runNow}
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function LegacyWorkflowsPage() {
  const { workloads } = useT();
  const t = workloads.legacy;
  const { org } = useCurrentOrg();
  const workflows = useQuery(workflowsQuery());
  const runs = useQuery(runsQuery());
  const environments = useQuery(environmentsQuery());
  const workspaces = useQuery(workspacesQuery());
  const mine = (workflows.data ?? []).filter((w) => org === undefined || w.org_id === org.id);
  const where = (w: Workflow) =>
    [environments.data?.find((e) => e.id === w.environment_id)?.name, workspaces.data?.find((x) => x.id === w.workspace_id)?.name]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      <PageHeader
        title={t.title}
        breadcrumb={sectionCrumbs("legacyWorkflows")}
        description={t.description}
      />
      {workflows.isPending ? <Loading /> : null}
      {workflows.isError ? <ErrorState message={workflows.error.message} /> : null}
      {workflows.isSuccess && mine.length === 0 ? (
        <EmptyState icon={<FlowArrow weight="duotone" />} title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      <div className="grid gap-5 lg:grid-cols-2 2xl:grid-cols-3">
        {mine.map((w) => (
          <WorkflowCard key={w.id} workflow={w} runs={(runs.data ?? []).filter((r) => r.workflow_id === w.id)} where={where(w)} />
        ))}
      </div>
    </>
  );
}
